/**
 * useCellChip — fetch a role cell + apply bindings + return resolved markdown.
 *
 * The chat surface's chip-click handler calls `loadCell(cellName, ctx)`;
 * the result is a string of resolved markdown ready for MarkdownRenderer.
 *
 * Per spec §3.4 this is the "fast path" that bypasses the LLM agent for
 * known intents (cost-control mechanism).
 */
import { useCallback } from 'react';
import { applyBindings, buildBindings, type BindingContext } from './bindings';

export interface CellChipContext {
  role: string;
  bindingCtx: BindingContext;
}

/** Strip a YAML front-matter block (`---\n...\n---`) from the start of a markdown string. */
export function stripFrontMatter(source: string): string {
  if (!source.startsWith('---\n')) return source;
  const end = source.indexOf('\n---\n', 4);
  if (end === -1) return source;
  return source.slice(end + 5);
}

export type LoadCellResult =
  | { ok: true; markdown: string }
  | { ok: false; error: string };

export async function loadCell(
  cellName: string,
  ctx: CellChipContext,
  fetchImpl: typeof fetch = fetch,
): Promise<LoadCellResult> {
  // Cell path: `content/{role}/cells/{cell}.md`. The cellName from the
  // role config is `{role}/{cell}` (e.g. `investor/portfolio-summary`);
  // strip the role prefix if present for url construction.
  const cellSlug = cellName.includes('/') ? cellName.split('/').pop()! : cellName;
  const url = `/content/${ctx.role}/cells/${cellSlug}.md`;

  let res: Response;
  try {
    res = await fetchImpl(url);
  } catch (err) {
    return { ok: false, error: `network: ${(err as Error).message}` };
  }
  if (!res.ok) return { ok: false, error: `cell not found: ${cellName}` };

  const raw = await res.text();
  const body = stripFrontMatter(raw);

  const bindings = buildBindings(ctx.bindingCtx);
  const resolved = applyBindings(body, bindings);

  return { ok: true, markdown: resolved };
}

/** Hook wrapper around `loadCell` for ergonomic React usage. */
export function useCellChip(role: string) {
  return useCallback(
    async (cellName: string, bindingCtx: BindingContext): Promise<LoadCellResult> => {
      return loadCell(cellName, { role, bindingCtx });
    },
    [role],
  );
}
